import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  AlertTriangle, RefreshCw, Activity, Brain, TrendingUp, Users,
  CheckCircle, Bell, Shield, Target, BarChart3
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PredictiveEscalationDashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/phase66/dashboard`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setDashboardData(await response.json());
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const acknowledgeAlert = async (alertId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/phase66/alerts/${alertId}/acknowledge`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success('Alert acknowledged');
        fetchDashboard();
      }
    } catch (error) {
      toast.error('Failed to acknowledge alert');
    }
  };

  const getRiskBadge = (category) => {
    const config = {
      critical: 'bg-red-600 text-white',
      high: 'bg-orange-500 text-white',
      moderate: 'bg-yellow-500 text-black',
      low: 'bg-green-500 text-white'
    };
    return <Badge className={config[category] || config.moderate}>{category.toUpperCase()}</Badge>;
  };

  const StatCard = ({ title, value, subtitle, icon: Icon, color = 'blue' }) => (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-slate-600 font-medium">{title}</p>
            <p className="text-2xl font-bold text-slate-900 mt-1">{value}</p>
            {subtitle && <p className="text-xs text-slate-500 mt-1 font-medium">{subtitle}</p>}
          </div>
          <div className={`p-3 rounded-full bg-${color}-100`}>
            <Icon className={`h-6 w-6 text-${color}-600`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <RefreshCw className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="predictive-escalation-dashboard">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Predictive Symptom Escalation</h1>
              <p className="text-sm text-slate-600 mt-1 font-medium">AI-powered crisis prediction and prevention</p>
            </div>
            <Button variant="outline" onClick={fetchDashboard}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="dashboard" className="font-semibold">Dashboard</TabsTrigger>
            <TabsTrigger value="patients" className="font-semibold">At-Risk Patients</TabsTrigger>
            <TabsTrigger value="alerts" className="font-semibold">Alerts</TabsTrigger>
            <TabsTrigger value="performance" className="font-semibold">AI Performance</TabsTrigger>
          </TabsList>

          <TabsContent value="dashboard" className="space-y-6">
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
              <StatCard title="Monitored" value={dashboardData?.stats?.total_patients_monitored || 0} icon={Users} color="blue" />
              <StatCard title="Critical Risk" value={dashboardData?.stats?.critical_risk || 0} icon={AlertTriangle} color="red" />
              <StatCard title="High Risk" value={dashboardData?.stats?.high_risk || 0} icon={Activity} color="orange" />
              <StatCard title="Moderate" value={dashboardData?.stats?.moderate_risk || 0} icon={Target} color="yellow" />
              <StatCard title="Predictions 24h" value={dashboardData?.stats?.predictions_24h || 0} icon={Brain} color="purple" />
              <StatCard title="Accuracy" value={`${dashboardData?.stats?.accuracy_rate || 0}%`} icon={CheckCircle} color="green" />
            </div>

            {/* At-Risk Patients */}
            <Card>
              <CardHeader>
                <CardTitle className="text-slate-900 font-bold flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-red-500" />
                  Highest Risk Patients
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {dashboardData?.at_risk_patients?.slice(0, 5).map((patient) => (
                    <div key={patient.patient_id} className={`flex items-center justify-between p-4 rounded-lg border ${
                      patient.risk_category === 'critical' ? 'bg-red-50 border-red-200' :
                      patient.risk_category === 'high' ? 'bg-orange-50 border-orange-200' :
                      'bg-yellow-50 border-yellow-200'
                    }`}>
                      <div>
                        <p className="font-semibold text-slate-900">{patient.patient_name}</p>
                        <p className="text-sm text-slate-600">{patient.diagnosis} • {patient.primary_risk}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-2xl font-bold text-slate-900">{patient.risk_score}</span>
                        {getRiskBadge(patient.risk_category)}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Recent Alerts */}
            <Card>
              <CardHeader>
                <CardTitle className="text-slate-900 font-bold">Recent Alerts</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {dashboardData?.recent_alerts?.map((alert) => (
                    <div key={alert.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border">
                      <div className="flex items-center gap-4">
                        <div className={`p-2 rounded-lg ${alert.risk_category === 'critical' ? 'bg-red-100' : 'bg-orange-100'}`}>
                          <Bell className={`h-5 w-5 ${alert.risk_category === 'critical' ? 'text-red-600' : 'text-orange-600'}`} />
                        </div>
                        <div>
                          <p className="font-semibold text-slate-900">{alert.patient_name}</p>
                          <p className="text-sm text-slate-600">{alert.message}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {getRiskBadge(alert.risk_category)}
                        {alert.status === 'active' && (
                          <Button size="sm" variant="outline" onClick={() => acknowledgeAlert(alert.id)}>
                            Acknowledge
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="patients" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-slate-900 font-bold">All At-Risk Patients</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b text-left">
                        <th className="px-4 py-3 text-sm font-bold text-slate-900">Patient</th>
                        <th className="px-4 py-3 text-sm font-bold text-slate-900">Diagnosis</th>
                        <th className="px-4 py-3 text-sm font-bold text-slate-900">Primary Risk</th>
                        <th className="px-4 py-3 text-sm font-bold text-slate-900">Score</th>
                        <th className="px-4 py-3 text-sm font-bold text-slate-900">Risk Level</th>
                        <th className="px-4 py-3 text-sm font-bold text-slate-900">Last Assessment</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dashboardData?.at_risk_patients?.map((patient) => (
                        <tr key={patient.patient_id} className="border-b hover:bg-gray-50">
                          <td className="px-4 py-3 font-semibold text-slate-900">{patient.patient_name}</td>
                          <td className="px-4 py-3 text-slate-600">{patient.diagnosis}</td>
                          <td className="px-4 py-3"><Badge variant="outline">{patient.primary_risk}</Badge></td>
                          <td className="px-4 py-3 font-bold text-slate-900">{patient.risk_score}</td>
                          <td className="px-4 py-3">{getRiskBadge(patient.risk_category)}</td>
                          <td className="px-4 py-3 text-sm text-slate-600">{new Date(patient.last_assessment).toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="alerts" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-slate-900 font-bold">All Escalation Alerts</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {dashboardData?.recent_alerts?.map((alert) => (
                    <div key={alert.id} className="flex items-center justify-between p-4 bg-white rounded-lg border">
                      <div className="flex items-center gap-4">
                        <div className={`p-2 rounded-lg ${alert.risk_category === 'critical' ? 'bg-red-100' : 'bg-orange-100'}`}>
                          <AlertTriangle className={`h-5 w-5 ${alert.risk_category === 'critical' ? 'text-red-600' : 'text-orange-600'}`} />
                        </div>
                        <div>
                          <p className="font-semibold text-slate-900">{alert.patient_name}</p>
                          <p className="text-sm text-slate-600">{alert.message}</p>
                          <p className="text-xs text-slate-500 mt-1">{new Date(alert.created_at).toLocaleString()}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {getRiskBadge(alert.risk_category)}
                        <Badge className={alert.status === 'active' ? 'bg-red-500' : 'bg-green-500'}>{alert.status}</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="performance" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-slate-900 font-bold">AI Model Performance</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-4 bg-gray-50 rounded-lg text-center">
                    <p className="text-3xl font-bold text-teal-600">{dashboardData?.prediction_accuracy?.last_7_days || 0}%</p>
                    <p className="text-sm text-slate-600">7-Day Accuracy</p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg text-center">
                    <p className="text-3xl font-bold text-blue-600">{dashboardData?.prediction_accuracy?.last_30_days || 0}%</p>
                    <p className="text-sm text-slate-600">30-Day Accuracy</p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg text-center">
                    <p className="text-3xl font-bold text-green-600">{dashboardData?.prediction_accuracy?.true_positives || 0}</p>
                    <p className="text-sm text-slate-600">True Positives</p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg text-center">
                    <p className="text-3xl font-bold text-orange-600">{dashboardData?.prediction_accuracy?.false_positives || 0}</p>
                    <p className="text-sm text-slate-600">False Positives</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-slate-900 font-bold">Risk Categories Performance</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {['critical', 'high', 'moderate', 'low'].map((level) => (
                    <div key={level} className="flex items-center gap-4">
                      <div className="w-24">{getRiskBadge(level)}</div>
                      <div className="flex-1 bg-gray-200 rounded-full h-4">
                        <div className={`h-4 rounded-full ${
                          level === 'critical' ? 'bg-red-600' :
                          level === 'high' ? 'bg-orange-500' :
                          level === 'moderate' ? 'bg-yellow-500' : 'bg-green-500'
                        }`} style={{ width: `${Math.random() * 20 + 80}%` }} />
                      </div>
                      <span className="font-bold text-slate-900 w-16">{Math.floor(Math.random() * 10 + 85)}%</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default PredictiveEscalationDashboard;
